import { Info } from 'lucide-react'
import React from 'react'

import { adminRecordInspector } from 'rails_routes'
import { CollapsibleContainer } from 'slab'

import { type MoneyAmount } from '../../types'
import { getFormattedDate, getLocaleCurrencyString } from '../../utils'
import { type ConsistentDeduction } from '../hooks/useGetMemberConsistentDeductions'
import { ThatchTooltip } from '../reusable/Tooltip'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'

type DeductionExplanationModalProps = {
  isOpen: boolean
  onClose: () => void
  pretaxDeduction?: ConsistentDeduction
  posttaxDeduction?: ConsistentDeduction
}

// Helper function to safely parse a monetary value
const safeParseMonetaryValue = (value: MoneyAmount | undefined | null): number => {
  if (!value || !value.value) return 0
  const parsed = parseFloat(value.value)
  return isNaN(parsed) ? 0 : parsed
}

// Helper function to calculate expected deduction per period
const calculatePerPeriodAmount = (totalAmount: number, periods: number | undefined): number => {
  if (!periods || periods <= 0) return 0
  return Math.round((totalAmount / periods) * 100) / 100 // Round to nearest cent
}

// Helper function to calculate remaining pay periods
const calculateRemainingPeriods = (remainingAmount: number, deductionAmount: number): number => {
  if (deductionAmount <= 0) return 0
  return Math.ceil(remainingAmount / deductionAmount)
}

// Helper function to get end of month date
const getEndOfMonth = (date: Date): Date => {
  const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0)
  return lastDay
}

const getStartOfMonth = (date: Date): Date => {
  return new Date(date.getFullYear(), date.getMonth(), 1)
}

// Label with tooltip component
const LabelWithTooltip = ({ label, tooltip }: { label: string, tooltip: string }) => (
  <span className='text-sm flex items-center'>
    {label}
    <span className='ml-1'>
      <ThatchTooltip
        text={tooltip}
        className='max-w-72 text-left'
        includeArrow
      >
        <Info className='w-4 h-4 text-gray-400 hover:text-gray-500 cursor-help' />
      </ThatchTooltip>
    </span>
  </span>
)

const DeductionExplanationSection = ({ deduction, type }: { deduction?: ConsistentDeduction, type: string }) => {
  if (!deduction) {
    return (
      <p className='text-gray-500'>No {type} deduction available</p>
    )
  }

  const expectedSpanAmount = safeParseMonetaryValue(deduction.expected_span_amount)
  const payPeriodsInSpan = deduction.pay_periods_in_span || 0
  const expectedPerPeriod = calculatePerPeriodAmount(expectedSpanAmount, payPeriodsInSpan)
  const spanStartDate = deduction.span_start_date ? getStartOfMonth(new Date(deduction.span_start_date)) : null
  const spanEndDate = deduction.span_end_date ? getEndOfMonth(new Date(deduction.span_end_date)) : null
  const spanMonths = spanEndDate && spanStartDate ? spanEndDate?.getMonth() - spanStartDate?.getMonth() + 1 : 'n/a'

  const expectedYtdAmount = safeParseMonetaryValue(deduction.expected_ytd_amount)
  const remainingAmount = expectedSpanAmount - expectedYtdAmount // Allow negative values

  const deductionAmount = safeParseMonetaryValue(deduction.amount)
  const correctionAmount = safeParseMonetaryValue(deduction.correction)

  const remainingPeriods = calculateRemainingPeriods(Math.max(0, remainingAmount), deductionAmount)

  const deductionLink = adminRecordInspector.show({ id: deduction.id })
  const expectedAnnualAmount = safeParseMonetaryValue(deduction.expected_annual_amount)
  const employerContribution = safeParseMonetaryValue(deduction.employer_contribution)
  const allowanceAmount = safeParseMonetaryValue(deduction.allowance_amount)

  return (
    <div className='space-y-3'>
      <div className='space-y-2'>
        <div className='flex justify-between'>
          <LabelWithTooltip
            label='Span start date:'
            tooltip='The start date of the period of consistent premiums and allowances'
          />
          <span className='font-medium'>{spanStartDate ? getFormattedDate(spanStartDate.toISOString(), { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : 'n/a'}</span>
        </div>
        <div className='flex justify-between'>
          <LabelWithTooltip
            label='Span end date:'
            tooltip='The end date of the period of consistent premiums and allowances'
          />
          <span className='font-medium'>{spanEndDate ? getFormattedDate(spanEndDate.toISOString(), { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : 'n/a'}</span>
        </div>
        <div className='flex justify-between'>
          <LabelWithTooltip
            label='Total months in span:'
            tooltip='The total number of months in the span (includes past months)'
          />
          <span className='font-medium'>{spanMonths}</span>
        </div>

      </div>

      <hr className='my-3 border-gray-200' />

      <div className='space-y-2'>
        <div className='flex justify-between'>
          <LabelWithTooltip
            label='Total amount owed during span:'
            tooltip="The total amount that needs to be deducted from the employee's paycheck during the entire coverage period. This is calculated based on the insurance premium and any applicable allowances."
          />
          <span className='font-medium'>{getLocaleCurrencyString(expectedSpanAmount)}</span>
        </div>
        <div className='flex justify-between'>
          <LabelWithTooltip
            label='Total number of periods during span:'
            tooltip="The number of pay periods that fall within the coverage period. This is determined by the employer's pay schedule and the coverage start/end dates."
          />
          <span className='font-medium'>{payPeriodsInSpan}</span>
        </div>

        <div className='flex justify-between'>
          <LabelWithTooltip
            label='Expected deduction amount per period (in span):'
            tooltip='The amount that should be deducted each pay period, calculated by dividing the total amount owed by the number of pay periods. This is rounded to the nearest cent.'
          />
          <div className='text-right'>
            <span className='font-medium'>{getLocaleCurrencyString(expectedPerPeriod)}</span>
            <div className='text-xs text-gray-500 mt-1'>
              {getLocaleCurrencyString(expectedSpanAmount)} / {payPeriodsInSpan} = {getLocaleCurrencyString(expectedPerPeriod)}
            </div>
          </div>
        </div>
      </div>

      <hr className='my-3 border-gray-200' />

      <div className='space-y-2'>
        <div className='flex justify-between'>
          <LabelWithTooltip
            label='Expected YTD deduction amount:'
            tooltip='The total amount that should have been deducted year-to-date based on the number of pay periods that have already occurred.'
          />
          <span className='font-medium'>{getLocaleCurrencyString(expectedYtdAmount)}</span>
        </div>

        <div className='flex justify-between'>
          <LabelWithTooltip
            label='Remaining amount owed during span:'
            tooltip='The amount still to be deducted for the remainder of the coverage period. Calculated as the total amount owed minus the expected YTD amount. Can be negative if more has been deducted than expected.'
          />
          <span className='font-medium'>{getLocaleCurrencyString(remainingAmount)}</span>
        </div>

        <div className='flex justify-between'>
          <LabelWithTooltip
            label='Remaining pay periods:'
            tooltip='The estimated number of pay periods needed to collect the remaining amount, based on the current deduction amount per period. Calculated by dividing the remaining amount by the deduction amount and rounding up.'
          />
          <span className='font-medium'>{remainingPeriods}</span>
        </div>
        <div className='flex justify-between'>
          <LabelWithTooltip
            label='Deduction amount:'
            tooltip="The actual amount being deducted from the employee's paycheck for this pay period. This may differ from the expected per-period amount due to corrections or adjustments."
          />
          <div className='text-right'>
            <span className='font-medium'>{getLocaleCurrencyString(deductionAmount)}</span>
            {remainingAmount > 0 && (
              <div className='text-xs text-gray-500 mt-1'>
                {getLocaleCurrencyString(Math.max(0, remainingAmount))} / {remainingPeriods} = {getLocaleCurrencyString(remainingAmount > 0 && remainingPeriods > 0 ? remainingAmount / remainingPeriods : 0)}
              </div>
            )}
          </div>
        </div>

        <div className='flex justify-between'>
          <LabelWithTooltip
            label='Correction amount:'
            tooltip='An adjustment amount applied to this deduction. Positive values indicate additional amounts being deducted to catch up on missed deductions. Negative values indicate refunds for over-deductions.'
          />
          <div className='text-right'>
            <span className='font-medium'>{getLocaleCurrencyString(correctionAmount)}</span>
            {correctionAmount !== 0 && (
              <div className='text-xs text-gray-500 mt-1'>
                {getLocaleCurrencyString(expectedPerPeriod)} - {getLocaleCurrencyString(deductionAmount)} = {getLocaleCurrencyString(expectedPerPeriod - deductionAmount)}
              </div>
            )}
          </div>
        </div>
      </div>

      {deduction.reported_deduction && (
        <>
          <hr className='my-3 border-gray-200' />
          <div className='space-y-2'>
            <h4 className='font-medium text-sm'>Reported Deduction</h4>
            <div className='flex justify-between'>
              <LabelWithTooltip
                label='Reported YTD amount:'
                tooltip='The total amount reported by the employer for this deduction type year-to-date.'
              />
              <span className='font-medium'>{getLocaleCurrencyString(safeParseMonetaryValue(deduction.reported_deduction.ytd_amount))}</span>
            </div>
            <div className='flex justify-between'>
              <LabelWithTooltip
                label='Reported through date:'
                tooltip='The date through which the reported YTD amount is through.'
              />
              <span className='font-medium'>{getFormattedDate(deduction.reported_deduction.through_date, { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })}</span>
            </div>
          </div>
        </>
      )}

      <hr className='my-3 border-gray-200' />

      <CollapsibleContainer heading='Math' defaultOpen={false}>
        <div className='flex flex-col items-center my-6 font-small text-medium w-full space-y-8 p-4'>
          <div className='flex items-center w-full justify-center'>
            <div className='inline-block text-center align-middle w-full'>
              <div className='block border-b-2 border-[#183147] px-2 pb-1 leading-snug w-full'>
                ((Amount Owed in Span + Amount Owed Before Span) - (reported + Thatch deductions YTD))
              </div>
              <div className='block px-2 pt-2'>
                Remaining Pay Periods In Span
              </div>
            </div>
            <span className='mx-6'>=</span>
            <span className='text-right whitespace-pre-line'>
              Deduction Amount
            </span>
          </div>
        </div>
      </CollapsibleContainer>
      <CollapsibleContainer heading='Issue Guesser 3000' defaultOpen={false}>
        <div className='flex flex-col items-center my-6 font-small text-medium w-full space-y-8 p-4'>
          <div className='flex flex-col w-full space-y-4'>
            {deduction && (
              <>
                <span>This is a guess as to why the deduction looks incorrect. It is not always correct.</span>
                {expectedAnnualAmount < expectedSpanAmount && (
                  <div className='p-4 bg-yellow-50 border border-yellow-200 rounded-md'>
                    <h4 className='font-medium text-yellow-800 mb-2'>Potential Coverage Change Detected</h4>
                    <p className='text-yellow-700'>
                      The annual amount is less than the span amount, which could indicate:
                    </p>
                    <ul className='list-disc list-inside mt-2 text-yellow-700'>
                      <li>Recent changes to ancillary coverage</li>
                      <li>Qualifying Life Events (QLEs) in the Member Benefit Portal</li>
                      <li>Changes in dependent coverage</li>
                    </ul>
                  </div>
                )}

                {expectedPerPeriod === deductionAmount && (
                  <div className='p-4 bg-green-50 border border-green-200 rounded-md'>
                    <h4 className='font-medium text-green-800 mb-2'>Deduction Looks Correct</h4>
                    <p className='text-green-700'>
                      The base amount matches the deduction amount, indicating no corrections or adjustments are needed.
                    </p>
                  </div>
                )}

                {employerContribution &&
                 employerContribution > allowanceAmount && (
                   <div className='p-4 bg-blue-50 border border-blue-200 rounded-md'>
                     <h4 className='font-medium text-blue-800 mb-2'>Extra Employer Contribution Detected</h4>
                     <p className='text-blue-700'>
                       The employer contribution exceeds the allowance amount. This likely means there is leftover balance in the member's account being applied to their deductions.
                     </p>
                   </div>
                )}

                {deduction.reported_deduction && (
                  <div className='p-4 bg-purple-50 border border-purple-200 rounded-md'>
                    <h4 className='font-medium text-purple-800 mb-2'>Reported Deduction Present</h4>
                    <p className='text-purple-700'>
                      A reported deduction is being used to calculate the YTD amount.
                    </p>
                  </div>
                )}

                {correctionAmount !== 0 && (
                  <div className='p-4 bg-red-50 border border-red-200 rounded-md'>
                    <h4 className='font-medium text-red-800 mb-2'>Correction Amount Detected</h4>
                    <p className='text-red-700'>
                      The correction amount is not zero, which indicates an adjustment is needed. Here are some other commons reasons to check:
                    </p>
                    <ul className='list-disc list-inside mt-2 text-yellow-700'>
                      <li>Employer pay schedule changes (ex. did they change frequency without submitting their YTD report?)</li>
                      <li>Changes to Insurance Enrollments or Premiums - were they updated recently?</li>
                      <li>Was the member terminated or did they lose coverage for another reason (ex. leave of absence, switching from full-time to part-time).</li>
                    </ul>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </CollapsibleContainer>
      <a
        href={deductionLink}
        target='_blank'
        rel='noopener noreferrer'
        className='text-indigo-600 hover:text-indigo-800 flex items-center'
      >
        <Info className='w-4 h-4 mr-1' /> View Deduction
      </a>
    </div>
  )
}

const DeductionExplanationModal: React.FC<DeductionExplanationModalProps> = ({
  isOpen,
  onClose,
  pretaxDeduction,
  posttaxDeduction
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className='max-w-5xl overflow-y-auto max-h-[90vh]'>
        <DialogHeader>
          <DialogTitle>Deduction Explainer 3000</DialogTitle>
        </DialogHeader>

        <div className='space-y-6 my-4'>
          {/* Pre-tax section */}
          <div className='border rounded-lg p-4'>
            <h3 className='text-lg font-semibold mb-2 flex items-center'>
              Pre-tax Deduction
              <span className='ml-2'>
                <ThatchTooltip
                  text="Pre-tax deductions are taken from an employee's paycheck before taxes are withheld. This reduces the employee's taxable income and results in tax savings."
                  className='max-w-72 text-left'
                  includeArrow
                >
                  <Info className='w-4 h-4 text-gray-400 hover:text-gray-500 cursor-help' />
                </ThatchTooltip>
              </span>
            </h3>
            <DeductionExplanationSection deduction={pretaxDeduction} type='pre-tax' />
          </div>

          {/* Post-tax section */}
          <div className='border rounded-lg p-4'>
            <h3 className='text-lg font-semibold mb-2 flex items-center'>
              Post-tax Deduction
              <span className='ml-2'>
                <ThatchTooltip
                  text="Post-tax deductions are taken from an employee's paycheck after taxes have been withheld. These deductions do not reduce the employee's taxable income."
                  className='max-w-72 text-left'
                  includeArrow
                >
                  <Info className='w-4 h-4 text-gray-400 hover:text-gray-500 cursor-help' />
                </ThatchTooltip>
              </span>
            </h3>
            <DeductionExplanationSection deduction={posttaxDeduction} type='post-tax' />
          </div>
        </div>

        <div className='mt-6 mb-4 space-y-2 text-sm'>
          <div>
            <a
              href='https://metabase.thatch.ai/question/2289-ytd-report-generator'
              target='_blank'
              rel='noopener noreferrer'
              className='text-indigo-600 hover:text-indigo-800 flex items-center'
            >
              <Info className='w-4 h-4 mr-1' /> Correction Report Generator
            </a>
          </div>
          <div>
            <a
              href='/admin/maintenance_tasks/tasks/Maintenance::Recurring::PayrollCreateReportedDeductions'
              target='_blank'
              rel='noopener noreferrer'
              className='text-indigo-600 hover:text-indigo-800 flex items-center'
            >
              <Info className='w-4 h-4 mr-1' /> Report Submission Task
            </a>
          </div>
        </div>

        <DialogFooter>
          <button
            onClick={onClose}
            className='px-4 py-2 bg-gray-200 rounded hover:bg-gray-300'
          >
            Close
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default DeductionExplanationModal
